import type {
  InstrumentDraftOption,
  InstrumentDraftQuestion,
} from '@/features/instruments/infra/instrumentDraft.api'
import { newObjectIdHex } from '@/features/instruments/domain/objectIdHex'
import { CHECKBOX_SELECT_ALL_CAPTION } from '@/features/instruments/domain/types'
import {
  composeWholeFieldMarkdown,
  defaultBuilderTextFormatFlags,
} from '@/features/instruments/domain/builderQuestionMarkdown'
import type { BuilderQuestion, PictureChoiceOptionRow } from './types'
import {
  isTextAnswerConfig,
  LIKERT_LABEL_MAX,
  pruneTextResponse,
  RANKING_ITEM_TEXT_MAX,
  SLIDER_END_LABEL_MAX,
  textResponseToDraft,
  type CheckboxConfig,
  type ChoiceTextResponseDraft,
  type MultipleChoiceConfig,
} from '@/features/instruments/domain'
import {
  effectiveSliderMaxValue,
  effectiveSliderMinValue,
  parseSliderStepField,
} from '@/features/instruments/domain/sliderRange'

const OBJECT_ID = /^[a-f0-9]{24}$/i

export function isInstrumentDraftApiEnabled(
  campaignId: string,
  instrumentId: string,
): boolean {
  if (!OBJECT_ID.test(instrumentId)) return false
  // BFP-999 quick instruments have no campaign — instrument id alone is enough.
  if (!campaignId.trim()) return true
  return OBJECT_ID.test(campaignId)
}

function parsePositiveInt(s: string): number | undefined {
  const t = s.trim()
  if (!t || !/^[1-9]\d*$/.test(t)) return undefined
  const n = Number(t)
  if (!Number.isFinite(n) || !Number.isInteger(n) || n < 1) return undefined
  return n
}

function parseSliderInt(s: string): number | undefined {
  const t = s.trim()
  if (!t || !/^-?(?:0|[1-9]\d*)$/.test(t)) return undefined
  const n = Number(t)
  if (!Number.isFinite(n) || !Number.isInteger(n)) return undefined
  return n
}

function mapQuestionType(
  t: BuilderQuestion['type'],
): InstrumentDraftQuestion['type'] | null {
  switch (t) {
    case 'MULTIPLE_CHOICE':
      return 'multiple_choice'
    case 'PICTURE_CHOICE':
      return 'picture_choice'
    case 'RANKING':
      return 'ranking'
    case 'YES_NO':
      return 'yes_no'
    case 'CHECKBOX':
      return 'checkbox'
    case 'DROPDOWN':
      return 'dropdown'
    case 'SHORT_TEXT':
      return 'short_text'
    case 'LONG_TEXT':
      return 'long_text'
    case 'DATE':
      return 'date'
    case 'RATING':
      return 'rating'
    case 'LIKERT':
      return 'likert'
    case 'SLIDER':
      return 'slider'
    case 'VIDEO':
      return 'video'
    default:
      return null
  }
}

function optionsFromLabelsAndIds(
  labels: readonly string[],
  ids: readonly string[] | undefined,
): InstrumentDraftOption[] {
  return labels.map((raw, index) => {
    const candidate = ids?.[index]?.trim() ?? ''
    const id = OBJECT_ID.test(candidate) ? candidate : newObjectIdHex()
    return {
      id,
      label: raw.trim().length > 0 ? raw.trim() : ' ',
    }
  })
}

/** BFP-1156 — always sent on MC / checkbox so `null` explicitly turns Add Text off. */
function choiceTextResponseForApi(
  d: MultipleChoiceConfig | CheckboxConfig,
  options: readonly InstrumentDraftOption[],
): ChoiceTextResponseDraft | null {
  const tr = pruneTextResponse(d.textResponse, {
    optionIds: options.map((o) => o.id),
    other: d.other,
    noneOfAbove: d.noneOfAbove,
  })
  return textResponseToDraft(tr)
}

function pictureChoiceOptionsToApi(
  rows: readonly PictureChoiceOptionRow[],
  labelMax = 2000,
): InstrumentDraftOption[] {
  return rows.map((r) => ({
    id: r.id,
    label: r.caption.trim().length > 0 ? r.caption.trim().slice(0, labelMax) : ' ',
    ...(r.imageUrl.trim() ? { imageUrl: r.imageUrl.trim() } : {}),
  }))
}

function optionalMinSelections(min: number | undefined) {
  return min === undefined ? {} : { minSelections: min }
}

function optionalMaxSelections(max: number | undefined) {
  return max === undefined ? {} : { maxSelections: max }
}

type DraftRest = Omit<InstrumentDraftQuestion, 'id' | 'order' | 'type' | 'prompt'>

function buildBase(q: BuilderQuestion): DraftRest {
  const plainDesc = q.description.trim()
  const fmtDesc = q.descriptionFormat ?? defaultBuilderTextFormatFlags
  const descComposed = composeWholeFieldMarkdown(plainDesc, fmtDesc).trim()
  return {
    mandatory: q.mandatory,
    hidden: q.hidden === true,
    ...(descComposed ? { description: descComposed } : {}),
    ...(q.fromTemplate ? { fromTemplate: true } : {}),
    ...(q.questionBankEntryId ? { questionBankEntryId: q.questionBankEntryId } : {}),
    ...(q.isProfileUpdate
      ? {
          isProfileUpdate: true,
          ...(q.profileAttributeId ? { profileAttributeId: q.profileAttributeId } : {}),
          freshnessTag: q.freshnessTag ?? 'PERM',
          ...(q.systemKey ? { systemKey: q.systemKey } : {}),
        }
      : {}),
  }
}

function mapYesNoForApi(q: BuilderQuestion, apiType: InstrumentDraftQuestion['type'], base: DraftRest): DraftRest | null {
  if (apiType === 'yes_no' && q.config.type === 'YES_NO') {
    const y = (q.config.data.yesLabel.trim() || 'Yes').slice(0, 50)
    const n = (q.config.data.noLabel.trim() || 'No').slice(0, 50)
    const d = q.config.data
    return {
      ...base,
      yesLabel: y,
      noLabel: n,
      yesNoToggleStyle: d.toggleStyle,
      layoutHorizontal: d.layoutHorizontal,
    }
  }
  return null
}

function mapMultipleChoiceForApi(
  q: BuilderQuestion,
  apiType: InstrumentDraftQuestion['type'],
  base: DraftRest,
): DraftRest | null {
  if (apiType === 'multiple_choice' && q.config.type === 'MULTIPLE_CHOICE') {
    const min = parsePositiveInt(q.config.data.minSelection)
    const max = parsePositiveInt(q.config.data.maxSelection)
    const d = q.config.data
    const options = optionsFromLabelsAndIds(d.options, d.optionIds)
    return {
      ...base,
      options,
      ...optionalMinSelections(min),
      ...optionalMaxSelections(max),
      randomizeOrder: d.randomize,
      layoutHorizontal: d.layoutHorizontal,
      multiColumn: d.multiColumn,
      hasOtherOption: d.other,
      hasNoneOfAboveOption: d.noneOfAbove,
      textResponse: choiceTextResponseForApi(d, options),
    }
  }
  return null
}

function mapCheckboxForApi(
  q: BuilderQuestion,
  apiType: InstrumentDraftQuestion['type'],
  base: DraftRest,
): DraftRest | null {
  if (apiType === 'checkbox' && q.config.type === 'CHECKBOX') {
    const min = parsePositiveInt(q.config.data.minSelection)
    const max = parsePositiveInt(q.config.data.maxSelection)
    const d = q.config.data
    const options = optionsFromLabelsAndIds(d.options, d.optionIds)
    return {
      ...base,
      options,
      ...optionalMinSelections(min),
      ...optionalMaxSelections(max),
      showSelectAll: d.selectAllLabel,
      ...(d.selectAllLabel ? { selectAllLabel: CHECKBOX_SELECT_ALL_CAPTION } : {}),
      randomizeOrder: d.randomize,
      layoutHorizontal: d.layoutHorizontal,
      multiColumn: d.multiColumn,
      hasOtherOption: d.other,
      hasNoneOfAboveOption: d.noneOfAbove,
      textResponse: choiceTextResponseForApi(d, options),
    }
  }
  return null
}

function mapDropdownForApi(
  q: BuilderQuestion,
  apiType: InstrumentDraftQuestion['type'],
  base: DraftRest,
): DraftRest | null {
  if (apiType === 'dropdown' && q.config.type === 'DROPDOWN') {
    const d = q.config.data
    return {
      ...base,
      options: optionsFromLabelsAndIds(d.options, d.optionIds),
      randomizeOrder: d.randomize,
      hasOtherOption: d.other,
      hasNoneOfAboveOption: d.noneOfAbove,
    }
  }
  return null
}

function mapPictureChoiceForApi(
  q: BuilderQuestion,
  apiType: InstrumentDraftQuestion['type'],
  base: DraftRest,
): DraftRest | null {
  if (apiType === 'picture_choice' && q.config.type === 'PICTURE_CHOICE') {
    return {
      ...base,
      imageSize: q.config.data.imageSize,
      options: pictureChoiceOptionsToApi(q.config.data.options),
      minSelections: 1,
      maxSelections: 1,
    }
  }
  return null
}

function mapRankingForApi(
  q: BuilderQuestion,
  apiType: InstrumentDraftQuestion['type'],
  base: DraftRest,
): DraftRest | null {
  if (apiType === 'ranking' && q.config.type === 'RANKING') {
    return {
      ...base,
      imageSize: q.config.data.imageSize,
      options: pictureChoiceOptionsToApi(q.config.data.options, RANKING_ITEM_TEXT_MAX),
    }
  }
  return null
}

function mapTextAnswerForApi(
  q: BuilderQuestion,
  apiType: InstrumentDraftQuestion['type'],
  base: DraftRest,
): DraftRest | null {
  if ((apiType === 'short_text' || apiType === 'long_text') && isTextAnswerConfig(q.config)) {
    const min = parsePositiveInt(q.config.data.minLength)
    const max = parsePositiveInt(q.config.data.maxLength)
    const ph = q.config.data.hintPlaceholder.trim()
    return {
      ...base,
      ...(min === undefined ? {} : { minLength: min }),
      ...(max === undefined ? {} : { maxLength: max }),
      ...(ph.length > 0 ? { placeholder: ph.slice(0, 500) } : {}),
    }
  }
  return null
}

function mapVideoForApi(
  q: BuilderQuestion,
  apiType: InstrumentDraftQuestion['type'],
  base: DraftRest,
): DraftRest | null {
  if (apiType !== 'video' || q.config.type !== 'VIDEO') return null
  const d = q.config.data
  const min = parsePositiveInt(d.minLength)
  const max = parsePositiveInt(d.maxLength)
  const ph = d.hintPlaceholder.trim()
  const url = d.videoUrl.trim()
  return {
    ...base,
    videoSource: d.sourceKind,
    ...(url ? { videoUrl: url.slice(0, 4096) } : {}),
    ...(min === undefined ? {} : { minLength: min }),
    ...(max === undefined ? {} : { maxLength: max }),
    ...(ph.length > 0 ? { placeholder: ph.slice(0, 500) } : {}),
  }
}

function mapDateForApi(
  q: BuilderQuestion,
  apiType: InstrumentDraftQuestion['type'],
  base: DraftRest,
): DraftRest | null {
  if (apiType === 'date' && q.config.type === 'DATE') {
    const d = q.config.data
    const e = d.earliestSelectableDate.trim()
    const l = d.latestSelectableDate.trim()
    const ph = d.hintPlaceholder.trim()
    return {
      ...base,
      ...(e ? { earliestSelectableDate: e } : {}),
      ...(l ? { latestSelectableDate: l } : {}),
      disablePastDates: d.disablePastDates,
      disableFutureDates: d.disableFutureDates,
      ...(ph.length > 0 ? { placeholder: ph.slice(0, 500) } : {}),
      dateInputFormat: d.displayFormat,
    }
  }
  return null
}

function mapRatingForApi(
  q: BuilderQuestion,
  apiType: InstrumentDraftQuestion['type'],
  base: DraftRest,
): DraftRest | null {
  if (apiType === 'rating' && q.config.type === 'RATING') {
    const d = q.config.data
    const low = d.lowLabel.trim()
    const high = d.highLabel.trim()
    return {
      ...base,
      ratingScaleMax: d.scaleMax,
      ratingDisplayStyle: d.displayStyle,
      ...(low ? { ratingLowLabel: low.slice(0, 100) } : {}),
      ...(high ? { ratingHighLabel: high.slice(0, 100) } : {}),
    }
  }
  return null
}

function mapLikertForApi(
  q: BuilderQuestion,
  apiType: InstrumentDraftQuestion['type'],
  base: DraftRest,
): DraftRest | null {
  if (apiType === 'likert' && q.config.type === 'LIKERT') {
    const d = q.config.data
    return {
      ...base,
      likertPointCount: d.pointCount,
      likertLabels: d.labels.map((lab) => lab.slice(0, LIKERT_LABEL_MAX)),
      likertLayoutHorizontal: d.layoutHorizontal,
    }
  }
  return null
}

function mapSliderForApi(
  q: BuilderQuestion,
  apiType: InstrumentDraftQuestion['type'],
  base: DraftRest,
): DraftRest | null {
  if (apiType === 'slider' && q.config.type === 'SLIDER') {
    const d = q.config.data
    const minV = parseSliderInt(d.minValue) ?? effectiveSliderMinValue(d.minValue)
    const maxV = parseSliderInt(d.maxValue) ?? effectiveSliderMaxValue(d.maxValue)
    const stepV = parseSliderStepField(d.step) ?? undefined
    if (minV === undefined || maxV === undefined || stepV === undefined) {
      throw new Error('Slider min, max, and step must be valid integers before persisting draft')
    }
    const low = d.lowLabel.trim()
    const high = d.highLabel.trim()
    return {
      ...base,
      sliderMinValue: minV,
      sliderMaxValue: maxV,
      sliderStep: stepV,
      ...(low ? { sliderLowLabel: low.slice(0, SLIDER_END_LABEL_MAX) } : {}),
      ...(high ? { sliderHighLabel: high.slice(0, SLIDER_END_LABEL_MAX) } : {}),
    }
  }
  return null
}

function mapConfigForApi(
  q: BuilderQuestion,
  apiType: InstrumentDraftQuestion['type'],
): Omit<InstrumentDraftQuestion, 'id' | 'order' | 'type' | 'prompt'> {
  const base = buildBase(q)
  const mappers: Array<(q: BuilderQuestion, t: InstrumentDraftQuestion['type'], b: DraftRest) => DraftRest | null> = [
    mapYesNoForApi,
    mapMultipleChoiceForApi,
    mapCheckboxForApi,
    mapDropdownForApi,
    mapPictureChoiceForApi,
    mapRankingForApi,
    mapTextAnswerForApi,
    mapDateForApi,
    mapRatingForApi,
    mapLikertForApi,
    mapSliderForApi,
    mapVideoForApi,
  ]
  for (const fn of mappers) {
    const out = fn(q, apiType, base)
    if (out) return out
  }
  return base
}

/**
 * Maps Sensemaker builder rows to campaign-service `PATCH …/draft` question array.
 * Question `id` and string-derived option ids must be 24-char hex Mongo ObjectIds (see `newObjectIdHex`).
 */
export function mapBuilderQuestionsToInstrumentDraft(
  questions: readonly BuilderQuestion[],
): InstrumentDraftQuestion[] {
  return questions.map((q, order) => {
    const apiType = mapQuestionType(q.type)
    if (!apiType) {
      throw new Error(`Unsupported question type for instrument draft API: ${q.type}`)
    }
    const fmtQ = q.questionTextFormat ?? defaultBuilderTextFormatFlags
    const prompt = composeWholeFieldMarkdown(q.questionText.trim(), fmtQ).trim()
    if (!prompt) {
      throw new Error('Question prompt is required before persisting draft')
    }
    const rest = mapConfigForApi(q, apiType)
    return {
      id: q.id,
      order,
      type: apiType,
      prompt,
      ...rest,
    }
  })
}
